
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Eye,
  FileClock,
  History,
  IdCard,
  LayoutTemplate,
  Loader2,
  RefreshCw,
  Search,
  ShieldCheck,
  Users,
} from 'lucide-react';
import { useSchool } from '../../../context/SchoolContext';
import { idCardApi } from '../services/idCardApi';
import {
  createBlobUrl,
  documentTypeForOwner,
  getApiErrorMessage,
  humanize,
  unwrapApiResponse,
} from '../utils/documentUtils';
import {
  EmptyState,
  ErrorBanner,
  LoadingBlock,
  Modal,
  PageHeader,
  Pagination,
  StatusBadge,
} from '../components/DocumentUi';

const PAGE_SIZE = 20;

const initialFilters = {
  q: '',
  class_level_id: '',
  section_id: '',
  staff_type: '',
  department: '',
  designation: '',
  photo_status: '',
  card_status: '',
};

const IDCardsPage = () => {
  const navigate = useNavigate();
  const { schoolId, activeAcademicYear, activeAcademicYearId } = useSchool();

  const [ownerType, setOwnerType] = useState('STUDENT');
  const [selectionMode, setSelectionMode] = useState('BULK');
  const [classLevels, setClassLevels] = useState([]);
  const [sections, setSections] = useState([]);
  const [templates, setTemplates] = useState([]);
  const academicYearId = activeAcademicYearId || '';
  const [templateId, setTemplateId] = useState('');
  const [filters, setFilters] = useState(initialFilters);
  const [candidates, setCandidates] = useState([]);
  const [selectedMap, setSelectedMap] = useState({});
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [outputMode, setOutputMode] = useState('BOTH');
  const [regenerateExisting, setRegenerateExisting] = useState(false);
  const [validation, setValidation] = useState(null);
  const [loadingMasters, setLoadingMasters] = useState(false);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [validating, setValidating] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewMode, setPreviewMode] = useState('TEMPLATE');
  const [previewCandidateIndex, setPreviewCandidateIndex] = useState(0);
  const [previewApproved, setPreviewApproved] = useState(false);
  const [error, setError] = useState('');

  const selectedCandidates = useMemo(() => Object.values(selectedMap), [selectedMap]);
  const selectedIds = useMemo(() => Object.keys(selectedMap), [selectedMap]);
  const selectedTemplate = useMemo(
    () => templates.find((template) => template.id === templateId) || null,
    [templateId, templates],
  );
  const filteredSections = useMemo(() => {
    if (!filters.class_level_id) return sections;
    return sections.filter(
      (section) => String(section.class_level_id || '') === String(filters.class_level_id),
    );
  }, [filters.class_level_id, sections]);

  const closePreview = useCallback(() => {
    setPreviewUrl((currentUrl) => {
      if (currentUrl) URL.revokeObjectURL(currentUrl);
      return '';
    });
  }, []);

  const resetPreviewApproval = useCallback(() => {
    setPreviewApproved(false);
    setPreviewCandidateIndex(0);
  }, []);

  const resetSelection = useCallback(() => {
    setSelectedMap({});
    setValidation(null);
    resetPreviewApproval();
    closePreview();
  }, [closePreview, resetPreviewApproval]);

  const loadMasters = useCallback(async () => {
    if (!schoolId) return;
    setLoadingMasters(true);
    setError('');
    try {
      const requests = [
        idCardApi.getTemplates(schoolId, documentTypeForOwner(ownerType)),
      ];
      if (ownerType === 'STUDENT' && academicYearId) {
        requests.push(
          idCardApi.getClassLevels(schoolId, {
            academicYearId,
            status: 'ACTIVE',
          }),
          idCardApi.getSections(schoolId, {
            academicYearId,
            status: 'ACTIVE',
          }),
        );
      }

      const responses = await Promise.all(requests);
      const templateRows = unwrapApiResponse(responses[0]).data || [];
      setTemplates(templateRows);
      const defaultTemplate =
        templateRows.find((item) => item.school_setting?.is_default) || templateRows[0];
      setTemplateId(defaultTemplate?.id || '');

      if (ownerType === 'STUDENT' && academicYearId) {
        const classes = unwrapApiResponse(responses[1]).data || [];
        const sectionRows = unwrapApiResponse(responses[2]).data || [];
        setClassLevels(classes);
        setSections(sectionRows);
      } else {
        setClassLevels([]);
        setSections([]);
      }
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, 'Failed to load ID-card setup data.'));
    } finally {
      setLoadingMasters(false);
    }
  }, [academicYearId, ownerType, schoolId]);

  const loadCandidates = useCallback(async () => {
    if (!schoolId) return;
    setLoadingCandidates(true);
    setError('');
    try {
      const response = await idCardApi.getCandidates(schoolId, {
        owner_type: ownerType,
        academic_year_id: ownerType === 'STUDENT' ? academicYearId : undefined,
        ...filters,
        status: 'ACTIVE',
        page,
        page_size: PAGE_SIZE,
      });
      const result = unwrapApiResponse(response);
      setCandidates(result.data || []);
      setPagination(result.pagination);
    } catch (requestError) {
      setCandidates([]);
      setPagination(null);
      setError(getApiErrorMessage(requestError, 'Failed to load ID-card candidates.'));
    } finally {
      setLoadingCandidates(false);
    }
  }, [academicYearId, filters, ownerType, page, schoolId]);

  useEffect(() => {
    resetSelection();
    setFilters(initialFilters);
    setPage(1);
    loadMasters();
  }, [loadMasters, resetSelection]);

  useEffect(() => {
    if (!loadingMasters && schoolId) loadCandidates();
  }, [loadingMasters, loadCandidates, schoolId]);

  useEffect(
    () => () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    },
    [previewUrl],
  );

  const updateFilter = (key, value) => {
    setFilters((previous) => ({ ...previous, [key]: value }));
    setPage(1);
  };

  const toggleCandidate = (candidate) => {
    setValidation(null);
    resetPreviewApproval();
    closePreview();
    setSelectedMap((previous) => {
      if (selectionMode === 'SINGLE') return { [candidate.owner_id]: candidate };
      const next = { ...previous };
      if (next[candidate.owner_id]) delete next[candidate.owner_id];
      else if (Object.keys(next).length < 500) next[candidate.owner_id] = candidate;
      return next;
    });
  };

  const toggleVisibleCandidates = () => {
    setValidation(null);
    resetPreviewApproval();
    closePreview();
    const allVisibleSelected = candidates.every((item) => selectedMap[item.owner_id]);
    setSelectedMap((previous) => {
      const next = { ...previous };
      candidates.forEach((candidate) => {
        if (allVisibleSelected) delete next[candidate.owner_id];
        else if (Object.keys(next).length < 500) next[candidate.owner_id] = candidate;
      });
      return next;
    });
  };

  const renderPreview = useCallback(async ({
    candidateIndex = 0,
    useSampleData = false,
    mode = 'TEMPLATE',
  } = {}) => {
    if (!templateId) return false;

    const safeIndex = Math.min(
      Math.max(candidateIndex, 0),
      Math.max(selectedCandidates.length - 1, 0),
    );
    const candidate = selectedCandidates[safeIndex];

    setPreviewing(true);
    setError('');
    try {
      const response = await idCardApi.previewTemplate(schoolId, templateId, {
        owner_type: ownerType,
        use_sample_data: useSampleData || !candidate,
        ...(!useSampleData && candidate ? { owner_id: candidate.owner_id } : {}),
        ...(ownerType === 'STUDENT' && academicYearId ? { academic_year_id: academicYearId } : {}),
      });

      closePreview();
      setPreviewCandidateIndex(safeIndex);
      setPreviewMode(mode);
      setPreviewUrl(createBlobUrl(response));
      return true;
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, 'Could not render the template preview.'));
      return false;
    } finally {
      setPreviewing(false);
    }
  }, [academicYearId, closePreview, ownerType, schoolId, selectedCandidates, templateId]);

  const handleValidate = async () => {
    if (!selectedIds.length) return;
    setValidating(true);
    setError('');
    resetPreviewApproval();
    closePreview();
    try {
      const response = await idCardApi.validateCandidates(schoolId, {
        owner_type: ownerType,
        owner_ids: selectedIds,
        ...(ownerType === 'STUDENT' && academicYearId ? { academic_year_id: academicYearId } : {}),
      });
      const result = unwrapApiResponse(response).data;
      setValidation(result);

      const passed =
        result && result.total === selectedIds.length && result.ineligible === 0;
      if (passed) {
        await renderPreview({ candidateIndex: 0, mode: 'VALIDATED' });
      }
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, 'Candidate validation failed.'));
    } finally {
      setValidating(false);
    }
  };

  const handlePreview = (useSampleData = false) =>
    renderPreview({
      candidateIndex: 0,
      useSampleData,
      mode: validationPassed && !useSampleData ? 'VALIDATED' : 'TEMPLATE',
    });

  const handlePreviewCandidateChange = async (direction) => {
    const nextIndex = previewCandidateIndex + direction;
    if (nextIndex < 0 || nextIndex >= selectedCandidates.length) return;
    await renderPreview({ candidateIndex: nextIndex, mode: 'VALIDATED' });
  };

  const handleApprovePreview = () => {
    setPreviewApproved(true);
    closePreview();
  };

  const handleGenerate = async () => {
    if (!selectedIds.length || !templateId) return;
    if (!validation || validation.ineligible > 0 || validation.total !== selectedIds.length) {
      setError('Validate the current selection and resolve all blocking errors before generation.');
      return;
    }
    if (!previewApproved) {
      setError('Preview and approve the validated ID card before creating the generation job.');
      return;
    }

    setGenerating(true);
    setError('');
    try {
      const response = await idCardApi.createGenerationJob(schoolId, {
        owner_type: ownerType,
        owner_ids: selectedIds,
        template_id: templateId,
        output_mode: selectionMode === 'SINGLE' ? 'INDIVIDUAL' : outputMode,
        regenerate_existing: regenerateExisting,
        ...(ownerType === 'STUDENT' && academicYearId ? { academic_year_id: academicYearId } : {}),
      });
      const job = unwrapApiResponse(response).data;
      navigate('/documents/generation-jobs', { state: { createdJobId: job?.id } });
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, 'Failed to create the generation job.'));
    } finally {
      setGenerating(false);
    }
  };

  const allVisibleSelected =
    candidates.length > 0 && candidates.every((candidate) => selectedMap[candidate.owner_id]);
  const validationPassed =
    validation && validation.total === selectedIds.length && validation.ineligible === 0;

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-6 pb-28">
      <PageHeader
        title="Generate ID Cards"
        description="Select student or staff records, validate eligibility, preview the final template, and start a generation job."
        actions={
          <>
            <Link className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50" to="/documents/templates">
              <LayoutTemplate className="h-4 w-4" /> Templates
            </Link>
            <Link className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50" to="/documents/generation-jobs">
              <FileClock className="h-4 w-4" /> Jobs
            </Link>
            <Link className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50" to="/documents/card-history">
              <History className="h-4 w-4" /> History
            </Link>
          </>
        }
      />

      <ErrorBanner message={error} onDismiss={() => setError('')} />

      {!schoolId ? (
        <ErrorBanner message="No school is selected for the logged-in user." />
      ) : loadingMasters ? (
        <div className="rounded-xl border border-gray-200 bg-white"><LoadingBlock label="Loading ID-card configuration..." /></div>
      ) : (
        <>
          <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
              <div className="flex flex-wrap gap-2">
                {['STUDENT', 'STAFF'].map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setOwnerType(type)}
                    className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold ${ownerType === type ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                  >
                    {type === 'STUDENT' ? <IdCard className="h-4 w-4" /> : <Users className="h-4 w-4" />}
                    {humanize(type)} cards
                  </button>
                ))}
              </div>

              <div className="flex flex-wrap items-end gap-3">
                {ownerType === 'STUDENT' && (
                  <div className="min-w-52 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-blue-500">Working Academic Year</p>
                    <p className="text-sm font-semibold text-blue-800">
                      {activeAcademicYear?.year_name || 'No academic year selected'}
                    </p>
                  </div>
                )}
                <div className="rounded-lg bg-gray-100 p-1">
                  {['SINGLE', 'BULK'].map((mode) => (
                    <button key={mode} type="button" onClick={() => { setSelectionMode(mode); resetSelection(); }} className={`rounded-md px-4 py-2 text-sm font-medium ${selectionMode === mode ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}>
                      {humanize(mode)}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h2 className="font-bold text-gray-900">1. Choose a template</h2>
                <p className="text-sm text-gray-500">The school default is preselected when configured.</p>
              </div>
              <button type="button" disabled={!templateId || previewing} onClick={() => handlePreview(selectedIds.length === 0)} className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                {previewing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />} Preview
              </button>
            </div>
            {templates.length ? (
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                {templates.map((template) => {
                  const isSelected = template.id === templateId;
                  const isDefault = Boolean(template.school_setting?.is_default);
                  return (
                    <button key={template.id} type="button" onClick={() => { setTemplateId(template.id); setValidation(null); resetPreviewApproval(); closePreview(); }} className={`rounded-xl border p-4 text-left transition ${isSelected ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-100' : 'border-gray-200 hover:border-gray-300'}`}>
                      <div className="mb-3 flex items-start justify-between gap-2">
                        <LayoutTemplate className={`h-6 w-6 ${isSelected ? 'text-blue-600' : 'text-gray-400'}`} />
                        {isDefault && <span className="rounded-full bg-gray-900 px-2 py-0.5 text-[10px] font-bold text-white">DEFAULT</span>}
                      </div>
                      <p className="font-semibold text-gray-900">{template.name}</p>
                      <p className="mt-1 text-xs text-gray-500">{humanize(template.orientation)} · {template.width_mm} × {template.height_mm} mm · {template.is_double_sided ? 'Double-sided' : 'Single-sided'}</p>
                    </button>
                  );
                })}
              </div>
            ) : (
              <EmptyState title="No templates available" description="Run the backend sync_document_templates command, then refresh this page." />
            )}
          </section>

          <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="border-b border-gray-200 p-5">
              <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <h2 className="font-bold text-gray-900">2. Select candidates</h2>
                  <p className="text-sm text-gray-500">Up to 500 records can be included in one generation job.</p>
                </div>
                <button type="button" onClick={loadCandidates} className="inline-flex items-center gap-2 self-start rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
                  <RefreshCw className={`h-4 w-4 ${loadingCandidates ? 'animate-spin' : ''}`} /> Refresh
                </button>
              </div>

              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                <label className="relative xl:col-span-2">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input value={filters.q} onChange={(event) => updateFilter('q', event.target.value)} onKeyDown={(event) => event.key === 'Enter' && loadCandidates()} placeholder={`Search ${ownerType === 'STUDENT' ? 'name or enrolment number' : 'name or employee ID'}`} className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm" />
                </label>
                {ownerType === 'STUDENT' ? (
                  <>
                    <select value={filters.class_level_id} onChange={(event) => { updateFilter('class_level_id', event.target.value); updateFilter('section_id', ''); }} className="rounded-lg border border-gray-300 px-3 py-2 text-sm">
                      <option value="">All classes</option>
                      {classLevels.map((item) => <option key={item.id} value={item.id}>{item.class_name}</option>)}
                    </select>
                    <select value={filters.section_id} onChange={(event) => updateFilter('section_id', event.target.value)} className="rounded-lg border border-gray-300 px-3 py-2 text-sm">
                      <option value="">All sections</option>
                      {filteredSections.map((item) => <option key={item.id} value={item.id}>{item.section_name}</option>)}
                    </select>
                  </>
                ) : (
                  <>
                    <input value={filters.department} onChange={(event) => updateFilter('department', event.target.value)} placeholder="Department" className="rounded-lg border border-gray-300 px-3 py-2 text-sm" />
                    <input value={filters.designation} onChange={(event) => updateFilter('designation', event.target.value)} placeholder="Designation" className="rounded-lg border border-gray-300 px-3 py-2 text-sm" />
                  </>
                )}
                <select value={filters.photo_status} onChange={(event) => updateFilter('photo_status', event.target.value)} className="rounded-lg border border-gray-300 px-3 py-2 text-sm">
                  <option value="">Any photo status</option>
                  <option value="AVAILABLE">Photo available</option>
                  <option value="MISSING">Photo missing</option>
                </select>
                <select value={filters.card_status} onChange={(event) => updateFilter('card_status', event.target.value)} className="rounded-lg border border-gray-300 px-3 py-2 text-sm">
                  <option value="">Any card status</option>
                  <option value="GENERATED">Generated</option>
                  <option value="NOT_GENERATED">Not generated</option>
                </select>
                {ownerType === 'STAFF' && (
                  <select value={filters.staff_type} onChange={(event) => updateFilter('staff_type', event.target.value)} className="rounded-lg border border-gray-300 px-3 py-2 text-sm">
                    <option value="">Any staff type</option>
                    <option value="TEACHING">Teaching</option>
                    <option value="NON_TEACHING">Non-teaching</option>
                  </select>
                )}
                <button type="button" onClick={() => { setFilters(initialFilters); setPage(1); }} className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Clear filters</button>
              </div>
            </div>

            {loadingCandidates ? (
              <LoadingBlock label="Loading candidates..." />
            ) : candidates.length === 0 ? (
              <EmptyState title="No candidates match these filters" description="Change the class, section, staff, photo, or card-status filters and try again." />
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-5 py-3">
                        {selectionMode === 'BULK' && <input type="checkbox" checked={allVisibleSelected} onChange={toggleVisibleCandidates} />}
                      </th>
                      <th className="px-5 py-3">Candidate</th>
                      <th className="px-5 py-3">Identifier</th>
                      <th className="px-5 py-3">Assignment</th>
                      <th className="px-5 py-3">Photo</th>
                      <th className="px-5 py-3">Card status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {candidates.map((candidate) => {
                      const selected = Boolean(selectedMap[candidate.owner_id]);
                      return (
                        <tr key={candidate.owner_id} onClick={() => toggleCandidate(candidate)} className={`cursor-pointer hover:bg-gray-50 ${selected ? 'bg-blue-50/60' : ''}`}>
                          <td className="px-5 py-4"><input type={selectionMode === 'SINGLE' ? 'radio' : 'checkbox'} checked={selected} onChange={() => toggleCandidate(candidate)} onClick={(event) => event.stopPropagation()} /></td>
                          <td className="px-5 py-4"><p className="font-semibold text-gray-900">{candidate.name}</p><p className="text-xs text-gray-500">{humanize(candidate.owner_type)}</p></td>
                          <td className="px-5 py-4 text-gray-600">{candidate.identifier || candidate.admission_no || '—'}</td>
                          <td className="px-5 py-4 text-gray-600">{ownerType === 'STUDENT' ? [candidate.class_name, candidate.section_name].filter(Boolean).join(' - ') || 'No academic assignment' : [candidate.designation, candidate.department].filter(Boolean).join(' · ') || '—'}</td>
                          <td className="px-5 py-4"><StatusBadge value={candidate.photo_available ? 'AVAILABLE' : 'MISSING'} /></td>
                          <td className="px-5 py-4"><StatusBadge value={candidate.card_status} /></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            <Pagination pagination={pagination} page={page} onPageChange={setPage} disabled={loadingCandidates} />
          </section>

          <section className="grid gap-6 lg:grid-cols-[1fr_360px]">
            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h2 className="font-bold text-gray-900">3. Validate selection</h2>
                  <p className="text-sm text-gray-500">Checks ownership, status, identifiers, photo, academic assignment, and existing cards.</p>
                </div>
                <button type="button" disabled={!selectedIds.length || validating} onClick={handleValidate} className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50">
                  {validating ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Validate {selectedIds.length || ''}
                </button>
              </div>

              {!selectedIds.length ? (
                <EmptyState title="No candidates selected" description="Choose one or more records from the candidate table." />
              ) : !validation ? (
                <div className="rounded-xl border border-dashed border-gray-300 p-8 text-center text-sm text-gray-500">{selectedIds.length} candidate{selectedIds.length === 1 ? '' : 's'} selected. Run validation before generation.</div>
              ) : (
                <div className="space-y-4">
                  <div className={`rounded-xl border p-4 ${validationPassed ? 'border-emerald-200 bg-emerald-50' : 'border-red-200 bg-red-50'}`}>
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className={`h-5 w-5 ${validationPassed ? 'text-emerald-600' : 'text-red-600'}`} />
                      <div><p className="font-semibold text-gray-900">{validation.eligible} eligible · {validation.ineligible} ineligible</p><p className="text-sm text-gray-600">{validationPassed ? 'All selected records are ready for generation.' : 'Resolve blocking errors or remove ineligible records.'}</p></div>
                    </div>
                  </div>
                  <div className="max-h-72 space-y-2 overflow-y-auto pr-1">
                    {validation.records?.map((record) => (
                      <div key={record.owner_id} className="rounded-lg border border-gray-200 p-3">
                        <div className="flex items-center justify-between gap-3"><p className="font-medium text-gray-900">{record.owner_name || selectedMap[record.owner_id]?.name || record.owner_id}</p><StatusBadge value={record.eligible ? 'AVAILABLE' : 'FAILED'} /></div>
                        {record.errors?.map((message) => <p key={message} className="mt-2 text-xs text-red-600">• {message}</p>)}
                        {record.warnings?.map((message) => <p key={message} className="mt-2 text-xs text-amber-700">• {message}</p>)}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <h2 className="font-bold text-gray-900">4. Preview & generate</h2>
              <p className="mb-4 text-sm text-gray-500">Approve a real rendered card before the backend job is created.</p>
              <div className="space-y-4">
                <label className="block text-sm font-medium text-gray-700">Output mode
                  <select disabled={selectionMode === 'SINGLE'} value={selectionMode === 'SINGLE' ? 'INDIVIDUAL' : outputMode} onChange={(event) => setOutputMode(event.target.value)} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2">
                    <option value="INDIVIDUAL">Individual PDFs</option>
                    <option value="BATCH">A4 batch PDF</option>
                    <option value="ZIP">ZIP of individual PDFs</option>
                    <option value="BOTH">Individual, batch and ZIP</option>
                  </select>
                </label>
                <label className="flex items-start gap-3 rounded-lg border border-gray-200 p-3 text-sm">
                  <input type="checkbox" checked={regenerateExisting} onChange={(event) => setRegenerateExisting(event.target.checked)} className="mt-0.5" />
                  <span><span className="font-medium text-gray-900">Regenerate existing active cards</span><span className="mt-0.5 block text-xs text-gray-500">Use only when replacing current cards through a new batch.</span></span>
                </label>
                <div className="rounded-lg bg-gray-50 p-3 text-sm text-gray-600"><p><span className="font-medium text-gray-900">Selected:</span> {selectedIds.length}</p><p className="mt-1"><span className="font-medium text-gray-900">Template:</span> {selectedTemplate?.name || 'Not selected'}</p></div>

                {validationPassed && !previewApproved && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                    <p className="font-semibold">Preview approval required</p>
                    <p className="mt-1 text-xs text-amber-800">Review the rendered card before creating the generation job.</p>
                    <button
                      type="button"
                      disabled={previewing}
                      onClick={() => renderPreview({ candidateIndex: 0, mode: 'VALIDATED' })}
                      className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-amber-300 bg-white px-3 py-2 font-semibold text-amber-900 hover:bg-amber-100 disabled:opacity-50"
                    >
                      {previewing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
                      Preview selected card
                    </button>
                  </div>
                )}

                {previewApproved && (
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
                    <div className="flex items-start gap-2">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                      <div>
                        <p className="font-semibold">Preview approved</p>
                        <p className="mt-1 text-xs text-emerald-800">The current selection and template are ready for generation.</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      disabled={previewing}
                      onClick={() => renderPreview({ candidateIndex: previewCandidateIndex, mode: 'VALIDATED' })}
                      className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-emerald-300 bg-white px-3 py-2 font-semibold text-emerald-900 hover:bg-emerald-100 disabled:opacity-50"
                    >
                      <Eye className="h-4 w-4" /> Review preview again
                    </button>
                  </div>
                )}

                <button type="button" disabled={!validationPassed || !previewApproved || !templateId || generating} onClick={handleGenerate} className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-gray-900 px-4 py-3 text-sm font-bold text-white hover:bg-black disabled:cursor-not-allowed disabled:opacity-40">
                  {generating ? <Loader2 className="h-5 w-5 animate-spin" /> : <IdCard className="h-5 w-5" />} Create generation job
                </button>
              </div>
            </div>
          </section>
        </>
      )}

      <Modal
        open={Boolean(previewUrl)}
        onClose={closePreview}
        title={`${selectedTemplate?.name || 'ID card'} preview`}
        description={
          previewMode === 'VALIDATED' && selectedCandidates[previewCandidateIndex]
            ? `${selectedCandidates[previewCandidateIndex].name} · ${previewCandidateIndex + 1} of ${selectedCandidates.length}. Rendered by the same backend renderer used for official generation.`
            : 'Rendered with the same backend renderer used for official generation.'
        }
        size="max-w-5xl"
        footer={
          previewMode === 'VALIDATED' ? (
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-2">
                {selectedCandidates.length > 1 && (
                  <>
                    <button
                      type="button"
                      disabled={previewing || previewCandidateIndex === 0}
                      onClick={() => handlePreviewCandidateChange(-1)}
                      className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                    >
                      <ChevronLeft className="h-4 w-4" /> Previous
                    </button>
                    <button
                      type="button"
                      disabled={previewing || previewCandidateIndex >= selectedCandidates.length - 1}
                      onClick={() => handlePreviewCandidateChange(1)}
                      className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                    >
                      Next <ChevronRight className="h-4 w-4" />
                    </button>
                  </>
                )}
              </div>
              <div className="flex items-center justify-end gap-2">
                <button type="button" onClick={closePreview} className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
                  Close
                </button>
                <button type="button" onClick={handleApprovePreview} className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-700">
                  <CheckCircle2 className="h-4 w-4" /> {selectedCandidates.length > 1 ? 'Approve sample preview' : 'Approve preview'}
                </button>
              </div>
            </div>
          ) : undefined
        }
      >
        <div className="relative">
          {previewing && (
            <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/80">
              <Loader2 className="h-7 w-7 animate-spin text-blue-600" />
            </div>
          )}
          <iframe title="ID card PDF preview" src={previewUrl} className="h-[68vh] w-full rounded-lg border border-gray-200" />
        </div>
      </Modal>
    </div>
  );
};

export default IDCardsPage;
